import { getCellContextFromName } from "./utils";
import { writeTableValue } from "../services/optilogicService";
import { gridInsertionInProgress } from "@/utils/eventBus";
import { isCosmicFrogNamedRange } from "./namedRangeUtils";

export const onCellChange = async (sheet = null) => {
  await window.Excel.run(async (context) => {
    const activeSheet =
      sheet || context.workbook.worksheets.getActiveWorksheet();
    activeSheet.load("name");
    await context.sync();

    await activeSheet.onChanged.removeAll();
    activeSheet.onChanged.add(async (eventArgs) => {
      console.log("✅ Cell changed at: ", eventArgs.address);
      if (gridInsertionInProgress.value) {
        console.log("Grid insertion in progress. Skipping cell change event.");
        return;
      }

      // Create a new Excel.run context for the event handler
      await window.Excel.run(async (eventContext) => {
        try {
          const sheet = eventContext.workbook.worksheets.getActiveWorksheet();
          sheet.load("name");
          await eventContext.sync();

          const address = `${sheet.name}!${eventArgs.address}`;
          console.log(`Cell changed: ${address}`);

          const names = sheet.names;
          names.load("items"); // Load just the list of names
          await eventContext.sync(); // 🟢 This sync is REQUIRED before accessing names.items
          console.log("sync items");

          // First, load the name and address for all ranges
          const namedRanges = names.items.map((name) => {
            const range = name.getRange();
            name.load("name");
            range.load("address");
            return { name, range };
          });
          await eventContext.sync(); // ✅ this syncs both name + address
          console.log("cache sync");

          // Find the matching Cosmic Frog named range
          let matchingName = null;
          for (const { name, range } of namedRanges) {
            console.log(`Checking name: ${name.name}, address: ${range.address}`);
            if (isCosmicFrogNamedRange(name.name) && range.address === address) {
              matchingName = name;
              console.log(`Match found: ${name.name}`);
              break;
            }
          }
          console.log("check on cache");
          if (matchingName) {
            const { modelName, tableName, itemId, propertyName } =
              getCellContextFromName(matchingName.name);

            const cell = sheet.getRange(address);
            cell.load("values");
            await eventContext.sync();

            const newValue = cell.values[0][0];
            console.log(`New value: ${newValue}`);

            const dataToWrite = {
              id: itemId,
              [propertyName]: String(newValue),
            };
            await writeTableValue(modelName, tableName, dataToWrite);
          } else {
            console.log("No matching name starting with 'olf_' found.");
          }
        } catch (error) {
          console.error("Error processing cell change:", error);
        }
      });
    });

    await context.sync();
  });
};

export const registerChangeHandlers = async () => {
  await window.Excel.run(async (context) => {
    const workbook = context.workbook;
    const sheets = workbook.worksheets;

    // Attach change event to all existing sheets
    sheets.load("items");
    await context.sync();

    for (const sheet of sheets.items) {
      await onCellChange(sheet);
    }

    sheets.onAdded.removeAll();
    // Listen for new sheets
    sheets.onAdded.add(async (eventArgs) => {
      console.log("📄 New sheet added:", eventArgs.worksheetId);

      // Wait for Excel to register the new worksheet fully
      await window.Excel.run(async (ctx) => {
        const newSheet = ctx.workbook.worksheets.getItem(eventArgs.worksheetId);
        await onCellChange(newSheet);
        await ctx.sync();
      });
    });

    await context.sync();
  });
};

export const getConfigValue = async (key) => {
  return await window.Excel.run(async (context) => {
    try {
      const sheet = context.workbook.worksheets.getItem("config");
      const usedRange = sheet.getUsedRange();
      usedRange.load("values");
      await context.sync();

      const rows = usedRange.values;

      // Loop through column A (index 0) to find the matching key
      for (let i = 0; i < rows.length; i++) {
        if (
          rows[i][0] &&
          rows[i][0].toString().trim().toLowerCase() ===
            key.trim().toLowerCase()
        ) {
          return rows[i][1] !== undefined ? rows[i][1].toString() : "";
        }
      }
      return "";
    } catch (error) {
      // If the sheet doesn't exist, Excel throws an ItemNotFound error
      if (error.code === window.Excel.ErrorCodes.itemNotFound) {
        console.warn("Config sheet not found.");
        return "";
      }
      // Log and rethrow any unexpected issues
      console.error("Unexpected error in getConfigValue:", error);
      throw error;
    }
  });
};
